// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getNotifyStyle } from "@eisinc/App/Components/Notify";
import { xPartNumberStyle } from "@eisinc/App/SharedStyles/ProductPartNumberStyle";
import { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import AddOutline from "@eisinc/XDL/Icons/AddOutline";
import Delete from "@eisinc/XDL/Icons/Delete";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { createLinkIconStyles, sharedProductDescriptionStyles, xScreen } from "@eisinc/XDL/Utilities";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

const priceStyle: TypographyPresentationProps = {
    variant: "p",
    ...fontConfig.singleLineSmall,
    weight: fontConfig.weight.medium,
    css: css`
        line-height: ${fontConfig.singleLineSmall.lineHeight};
    `,
};

const certBadgeStyle = (color: string) => {
    return css`
        border: 1px solid ${getColor(color)};
        border-radius: 10px;
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: nowrap;
        font-size: ${fontConfig.singleLinexSmall.size}px;
        line-height: ${fontConfig.singleLinexSmall.lineHeight};
        font-weight: ${fontConfig.weight.medium};
        padding-inline: 6px;
        height: 18px;
        white-space: nowrap;
        ${IconWrapper} {
            color: ${getColor(color)};
        }
    `;
};

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    longProductDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    qtyValue?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: TypographyProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    brandStyles?: ProductBrandStyles;
    removeLink?: LinkPresentationProps;
    productAvailabilityWrapper?: InjectableCss;
    badgesWrapper?: InjectableCss;
    productInfoGridItem?: InjectableCss;
    certBadgesWrapper?: InjectableCss;
    mfrBadge?: InjectableCss;
    eisBadge?: InjectableCss;
    wishListAndCheckboxes?: InjectableCss;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 92px;
                height: 92px;
                position: relative;
                &&& > img {
                    aspect-ratio: 1;
                    object-fit: contain;
                    font-size: 14px;
                }

                @media print {
                    display: none;
                }
            `,
        },
        image: {
            css: css`
                width: auto;
                height: auto;
                max-width: 92px;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            `,
        },
    },
    productAvailabilityWrapper: {
        css: css`
            grid-column: 2/3;
            grid-row: 2/3;
            ${xScreen("lte", "sm")} {
                grid-row: auto;
                grid-column: 1/3;
            }
        `,
    },
    productDescription: {
        ...sharedProductDescriptionStyles({
            styles: {
                productDetailDescription: {
                    css: css`
                        grid-column: 1/3;
                    `,
                },
                productDetailLink: {
                    css: css`
                        grid-column: 1/3;
                    `,
                },
            },
        }),
    },
    longProductDescription: {
        ...sharedProductDescriptionStyles({
            lines: 3,
            styles: {
                productDetailDescription: {
                    css: css`
                        grid-column: 1/3;
                    `,
                },
                productDetailLink: {
                    css: css`
                        grid-column: 1/3;
                    `,
                },
            },
        }),
    },
    brandStyles: {},
    addToListLink: {
        ...createLinkIconStyles({
            icon: AddOutline,
        }),
    },
    productPrice: {
        priceLabelText: {
            ...fontConfig.singleLinexSmall,
            color: "text",
            transform: "capitalize",
            css: css`
                display: none;
                margin-bottom: 4px;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    display: block;
                }
            `,
        },
        price: {
            priceText: priceStyle,
            errorText: priceStyle,
            unitOfMeasure: {
                unitOfMeasureText: {
                    ...fontConfig.singleLinexSmall,
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
        },
        invalidPriceText: {
            ...priceStyle,
            color: "danger",
        },
        quoteMessage: {
            text: priceStyle,
        },
        priceWrapper: {
            css: css`
                flex-wrap: wrap;
            `,
        },
    },
    extendedUnitNetPriceText: {
        weight: fontConfig.weight.medium,
        ...fontConfig.singleLineSmall,
    },

    costCodeGridItem: {
        css: css`
            grid-column: 1/3;
            grid-row: 3/4;
            &:empty {
                display: none;
            }
        `,
    },
    costCodeLabelText: {
        ...xPartNumberStyle.label,
    },
    costCodeValueText: {
        ...xPartNumberStyle.value,
    },
    configurationOptionText: {
        ...xPartNumberStyle.value,
    },
    configurationOptionLabel: {
        ...xPartNumberStyle.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 8px;
        `,
    },
    promotionNameLabel: {
        ...xPartNumberStyle.label,
    },
    promotionNameText: {
        ...xPartNumberStyle.value,
    },
    qtyLabel: {
        as: "label",
        color: "text.main",
        ...fontConfig.singleLinexSmall,
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
        `,
    },
    qtyValue: {
        ...fontConfig.singleLineSmall,
        weight: fontConfig.weight.medium,
    },
    removeLink: {
        ...createLinkIconStyles({
            icon: Delete,
            linkStyles: {
                typographyProps: {
                    css: css`
                        display: none;
                        ${xScreen("lte", "md")} {
                            display: initial;
                        }
                    `,
                },
            },
        }),
    },

    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        ...getNotifyStyle("danger"),
    },
    unitOfMeasure: {
        ...fontConfig.singleLinexSmall,
        css: css`
            line-height: ${fontConfig.singleLinexSmall.lineHeight};
            text-transform: capitalize;
        `,
    },
    badgesWrapper: {
        css: css`
            grid-column: 1/3;
            grid-row: 4/5;
            display: flex;
            column-gap: 6px;
            &:empty {
                display: none;
            }
            .popup-content-wrapper {
                display: flex;
                max-width: 250px;
                span {
                    margin: 0;
                }
            }
        `,
    },
    productInfoGridItem: {
        css: css`
            display: grid;
            width: 100%;
            grid-template-columns: 1fr auto;
            row-gap: 8px;
        `,
    },
    certBadgesWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            row-gap: 6px;
            &:empty {
                display: none;
            }
        `,
    },
    eisBadge: {
        css: certBadgeStyle("primary"),
    },
    mfrBadge: {
        css: certBadgeStyle("common.ui4"),
    },
    wishListAndCheckboxes: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 8px;
        `,
    },
};

export default cartLineSharedStyles;
